import moment from 'moment'
import { computed, ref, type ComputedRef, type Ref } from 'vue'

import { useSystemStore } from '@/client/store/useSystemStore'
import {
  CSV_MAX_TOTAL,
  CSV_PAGE_SIZE,
  GetTwitterBenchmarkCampaignPostsCsvQuery,
  PostCsvResponse,
  SearchFilters
} from '@/typings/twitter_benchmark_campaign_posts'

import { getTwBenchmarkPostsCsv } from '../api/getTwBenchmarkPostsCsv'
import { useTwBenchmarkPostsStore } from '../store/useTwBenchmarkPostsStore'
import { formatCampaignType, formatIncentive } from '../utils/campaignLabels'
import { formatDateRange, formatDateTime, formatPercent } from '../utils/formatters'

export type CsvDownloadResult =
  | { data: (string | number)[][]; filename: string }
  | { error: string; title?: string; message?: string }

type CsvPost = PostCsvResponse['posts'][number]

const CSV_HEADER: string[] = [
  'データ取得日時',
  'キャンペーン期間',
  '開催日数',
  'アカウントID',
  'スクリーン名',
  '名前',
  '業界',
  'タグ',
  '投稿時点のフォロワー数',
  'キャンペーン投稿URL',
  'キャンペーン投稿メディア',
  '投稿内容',
  'キャンペーン形式',
  'インセンティブ',
  '当選人数',
  'トレンド実績',
  'トレンド最高到達順位',
  'トレンド最高到達キーワード',
  'キャンペーン詳細',
  'インプレッション',
  'いいね数',
  'リポスト数',
  '引用数',
  '返信数',
  '反応数',
  '反応率',
  '投稿日時'
]

const orEmpty = (value: string | number | null | undefined): string | number =>
  value === null || value === undefined ? '' : value

const formatCsvWinnerCount = (value: number | null | undefined): string | number =>
  value === null || value === undefined || value === 0 ? '-' : value

const formatIndustry = (industry: CsvPost['industry']): string => {
  if (!industry) return ''
  return [industry.majorCategory, industry.minorCategory].filter(Boolean).join(' / ')
}

const toCsvRow = (item: CsvPost): (string | number)[] => {
  const { post, account, metrics } = item
  return [
    formatDateTime(post.lastSyncedAt),
    formatDateRange(post.campaignStartDatetime, post.campaignEndDatetime),
    orEmpty(post.campaignDays),
    account ? orEmpty(account.id) : '',
    account ? orEmpty(account.screenName) : '',
    account ? orEmpty(account.name) : '',
    formatIndustry(item.industry),
    item.tags.map(tag => tag.name).join(' / '),
    account ? orEmpty(account.followersCount) : '',
    orEmpty(post.postUrl),
    orEmpty(post.previewPostMediaUrl),
    orEmpty(post.postText),
    formatCampaignType(post.campaignType),
    formatIncentive(post.incentive),
    formatCsvWinnerCount(post.winnerCount),
    post.isTrend ? 'あり' : 'なし',
    orEmpty(item.highestTrendRanking),
    orEmpty(item.highestTrendKeyword),
    orEmpty(post.campaignDetail),
    orEmpty(metrics.impressionsCount),
    orEmpty(metrics.likesCount),
    orEmpty(metrics.retweetsCount),
    orEmpty(metrics.quotesCount),
    orEmpty(metrics.repliesCount),
    orEmpty(metrics.reactionsCount),
    formatPercent(metrics.reactionsRate),
    formatDateTime(post.publishDatetime)
  ]
}

export type UseTwBenchmarkCsvDownload = {
  canDownload: ComputedRef<boolean>
  isDownloading: Ref<boolean>
  progressText: Ref<string>
  download: () => Promise<CsvDownloadResult>
}

/**
 * 競合分析投稿の CSV ダウンロード。サーバに状態を持たせず、現在の検索条件で CSV 用 API を CSV_PAGE_SIZE 件ずつ取得して結合する。
 */
export function useTwBenchmarkCsvDownload(): UseTwBenchmarkCsvDownload {
  const systemStore = useSystemStore()
  const postsStore = useTwBenchmarkPostsStore()

  const isDownloading = ref(false)
  const progressText = ref('')

  const setProgress = (acquired: number, total: number): void => {
    progressText.value = `CSVファイルの作成中です。しばらくお待ちください。\n ${acquired}件 / ${total}件`
  }

  const canDownload = computed<boolean>(() => Boolean(systemStore.currentGroup?.isBenchmarkAnalyticsCsvDownload))

  const buildQuery = (groupId: number, offset: number): GetTwitterBenchmarkCampaignPostsCsvQuery => {
    const filters = { ...postsStore.searchFilters } as SearchFilters
    return {
      ...filters,
      groupId,
      startDatetime: moment(filters.startDatetime).format(),
      endDatetime: moment(filters.endDatetime).format(),
      sortBy: postsStore.sortBy,
      order: postsStore.order,
      limit: CSV_PAGE_SIZE,
      offset
    }
  }

  const download = async (): Promise<CsvDownloadResult> => {
    const groupId = systemStore.currentGroupId
    if (typeof groupId !== 'number') {
      return { error: 'NO_GROUP' }
    }
    if (!canDownload.value) {
      return { error: 'PERMISSION_DENIED' }
    }

    isDownloading.value = true
    setProgress(0, 0)

    try {
      const rows: CsvPost[] = []
      let total = 0
      let offset = 0

      for (;;) {
        const result = await getTwBenchmarkPostsCsv(buildQuery(groupId, offset))
        if (!result.isSuccess || !result.data) {
          return { error: 'FETCH_FAILED' }
        }

        total = Math.min(result.data.totalCount, CSV_MAX_TOTAL)
        rows.push(...result.data.posts)
        setProgress(Math.min(rows.length, total), total)

        offset += CSV_PAGE_SIZE
        if (rows.length >= total || result.data.posts.length < CSV_PAGE_SIZE) {
          break
        }
      }

      const limited = rows.slice(0, CSV_MAX_TOTAL)
      const data: (string | number)[][] = [CSV_HEADER, ...limited.map(toCsvRow)]
      const filename = `競合キャンペーン分析 ${moment().format('YYYY年M月D日')}`

      return { data, filename }
    } finally {
      isDownloading.value = false
      progressText.value = ''
    }
  }

  return { canDownload, isDownloading, progressText, download }
}
